// Used by both SDKs. Never send payloads, identities, or database command arguments.
const PRIVATE_FIELD = /authorization|api.?key|cookie|password|secret|token|email|user|customer|billing|headers?|body|query_string|url\.(query|fragment)|params|arguments|^vars$|db\.(query|statement|redis)|cache\.(key|item)/i;
const DATABASE_METADATA = new Set(['db.system', 'db.system.name', 'db.operation', 'db.operation.name', 'server.port']);

/** Remove query strings, fragments, URL credentials, and identifiers from diagnostic text. */
function scrubText(value) {
  return value
    .replace(/(\/\/)[^/\s@]+@/g, '$1[Filtered]@')
    .replace(/[?#][^\s"'<>)]*/g, '')
    .replace(/\bBearer\s+[^\s"'<>]+/gi, 'Bearer [Filtered]')
    .replace(/[\w.+%-]+(?:@|%40)[\w.-]+/gi, '[Filtered]')
    .replace(/\b[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}\b/gi, '[Filtered]')
    .replace(/\b[\da-f]{24}\b/gi, '[Filtered]')
    .replace(/\beyJ[\w-]+\.[\w-]+\.[\w-]+\b/g, '[Filtered]')
    .replace(/\b(?:pay|cus|cks|cs|txn)_[\w-]+\b/g, '[Filtered]');
}

/** Recursively remove sensitive attributes, including manually attached context. */
function scrubData(value, seen = new WeakSet(), depth = 0) {
  if (typeof value === 'string') return scrubText(value);
  if (!value || typeof value !== 'object') return value;
  if (seen.has(value) || depth > 20) return '[Filtered]';
  seen.add(value);
  if (Array.isArray(value)) return value.map(item => scrubData(item, seen, depth + 1));
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !PRIVATE_FIELD.test(key))
    .map(([key, item]) => [key, scrubData(item, seen, depth + 1)]));
}

/** Keep database timing/operation metadata without keys, statements, or command values. */
export function scrubSpan(span) {
  const clean = scrubData(span);
  if (span.op?.startsWith('db') || span.data?.['db.system'] || span.data?.['db.system.name']) {
    clean.description = 'Database operation';
    clean.data = Object.fromEntries(Object.entries(clean.data || {})
      .filter(([key]) => DATABASE_METADATA.has(key)));
  }
  return clean;
}

/** Keep only navigation/network breadcrumbs; console, UI text, and free-form messages contain customer data. */
export function scrubBreadcrumb(breadcrumb) {
  if (!['http', 'fetch', 'xhr', 'navigation'].includes(breadcrumb.category)) return null;
  return {
    type: breadcrumb.type,
    category: breadcrumb.category,
    level: breadcrumb.level,
    timestamp: breadcrumb.timestamp,
    data: scrubData(Object.fromEntries(Object.entries(breadcrumb.data || {})
      .filter(([key]) => ['method', 'status_code', 'url', 'from', 'to'].includes(key))))
  };
}

/** Scrub errors and static transactions at the final transport boundary. */
export function scrubEvent(event) {
  const clean = scrubData(event);
  delete clean.user;
  delete clean.extra;
  delete clean.logentry;
  // Error/provider messages can echo arbitrary request values. Types and stacks remain useful.
  if (clean.message) clean.message = '[Filtered]';
  for (const exception of clean.exception?.values || []) {
    exception.value = '[Filtered]';
  }
  if (event.request) {
    clean.request = scrubData({ method: event.request.method, url: event.request.url });
  }
  if (clean.contexts) {
    clean.contexts = Object.fromEntries(Object.entries(clean.contexts)
      .filter(([key]) => ['trace', 'runtime', 'browser', 'os', 'device'].includes(key)));
  }
  if (event.breadcrumbs) clean.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb).filter(Boolean);
  if (event.spans) clean.spans = event.spans.map(scrubSpan);
  if (event.contexts?.trace?.op?.startsWith('db')) {
    clean.transaction = 'Database operation';
    clean.contexts.trace.data = scrubSpan({ op: 'db', data: event.contexts.trace.data }).data;
    if (clean.sdkProcessingMetadata?.dynamicSamplingContext) {
      clean.sdkProcessingMetadata.dynamicSamplingContext.transaction = clean.transaction;
    }
  }
  return clean;
}

/** Apply the same collection limits and final hooks to the Node and React SDKs. */
export function sentryPrivacyOptions(Sentry) {
  return {
    traceLifecycle: 'static',
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: false, response: false },
      httpBodies: [],
      urlQueryParams: false,
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      graphQL: { document: false, variables: false },
      frameContextLines: 7,
    },
    beforeSend: scrubEvent,
    beforeSendTransaction: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSendSpan: Sentry.withStaticSpan(scrubSpan),
  };
}
